import { Box } from "@mui/material";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from "recharts";
import { useReportData } from "./UseReportData";
import { ChartCard, Empty, KpiBand, SectionFrame, SimpleTable, StatusKey, bucketLabel, bucketTitle, fmt, fmtPct, shortNum, statusOf, useChartColors, varPct } from "./Reportkit";

function Body({ d }) {
  const c = useChartColors();
  const t = d.totals, p = d.previousTotals;
  const lpt = t.tonnes ? t.truckLitres / t.tonnes : null, lptP = p.tonnes ? p.truckLitres / p.tonnes : null;
  const lgoh = t.goh ? t.truckLitres / t.goh : null, lgohP = p.goh ? p.truckLitres / p.goh : null;
  const trend = d.trend.map((x) => ({ label: bucketLabel(x.bucket, d.grain), title: bucketTitle(x.bucket, d.grain), litres: x.litres }));
  const share = (rows) => rows.map((r) => ({ ...r, pct: t.litres ? (r.litres / t.litres) * 100 : 0 }));
  const mobile = d.bySource.filter((s) => /mobile/i.test(s.name)).reduce((s, r) => s + r.litres, 0);
  const cmp = (v, pv) => ({ status: statusOf(v, pv, "lower"), delta: varPct(v, pv) });
  return (
    <>
      <KpiBand items={[
        { label: "Fuel dispensed", value: `${fmt(t.litres / 1000, 1)} kL`, sub: `Prev ${fmt(p.litres / 1000, 1)} kL`, ...cmp(t.litres, p.litres) },
        { label: "Litres per tonne", value: lpt == null ? "—" : `${fmt(lpt, 3)} L/t`, sub: `Prev ${fmt(lptP, 3)}`, ...cmp(lpt, lptP) },
        { label: "Burn rate", value: lgoh == null ? "—" : `${fmt(lgoh, 1)} L/GOH`, sub: `Prev ${fmt(lgohP, 1)}`, ...cmp(lgoh, lgohP) },
        { label: "Fuel entries", value: fmt(t.entries), sub: t.entries ? `${fmt(t.litres / t.entries)} L / entry` : "" },
        { label: "Mobile dispensing", value: fmtPct(t.litres ? (mobile / t.litres) * 100 : null, 1), sub: "source name contains 'mobile'" },
      ]} />
      <ChartCard title="Fuel dispensed" sub={`Litres by ${d.grain}`}>
        {trend.length === 0 ? <Empty /> : (
          <ResponsiveContainer width="100%" height={270}>
            <BarChart data={trend} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={c.grid} />
              <XAxis dataKey="label" tick={c.tick} minTickGap={14} /><YAxis tick={c.tick} width={56} tickFormatter={shortNum} />
              <RTooltip formatter={(v) => `${fmt(v)} L`} labelFormatter={(l, pl) => pl?.[0]?.payload?.title || l} />
              <Bar dataKey="litres" fill={c.series[0]} maxBarSize={46} radius={[4, 4, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
        <ChartCard title="By fuel source" sub="Litres and share">
          <SimpleTable max={260} cols={[{ key: "name", label: "Source", align: "left" }, { key: "litres", label: "Litres", render: (r) => fmt(r.litres) }, { key: "pct", label: "Share", render: (r) => fmtPct(r.pct, 1) }, { key: "entries", label: "Entries", render: (r) => fmt(r.entries) }]} rows={share(d.bySource)} />
        </ChartCard>
        <ChartCard title="Top consuming units" sub="Litres in the period">
          <SimpleTable max={260} cols={[{ key: "name", label: "Unit", align: "left" }, { key: "model", label: "Model", align: "left" }, { key: "litres", label: "Litres", render: (r) => fmt(r.litres) }, { key: "entries", label: "Entries", render: (r) => fmt(r.entries) }]} rows={d.topUnits} />
        </ChartCard>
      </Box>
      <StatusKey basis="prev" />
      <span style={{ fontSize: 12, opacity: 0.7 }}>No fuel plan exists, so status compares with the previous period of equal length (lower is better). L/t and L/GOH use fuel dispensed to haul trucks only. A fuel entry is a refill, so short periods are noisy.</span>
    </>
  );
}

export default function FuelSection({ req }) {
  const st = useReportData("fuel", req);
  return (
    <SectionFrame eyebrow="Diesel" title="Fuel" desc="Dispensing, intensity per tonne and per operating hour, and top consumers" state={st}>
      {st.data && <Body d={st.data} />}
    </SectionFrame>
  );
}